import { useEffect } from 'react'
import Head from 'next/head'
import Script from 'next/script'
import { ModernHomePage } from '../../components/index/ModernHomePage'
import {
    legalName,
    branchAddress,
    branchGeo,
} from '../../utils/business-info'
import { COUNTRY_HOME_ALTERNATES } from '../../utils/markets'
import { breadcrumbJsonLd, buildBreadcrumb } from '../../utils/breadcrumb'

// Singapore home. Until Oct 2026 this was "/", which is now the global page
// for all three countries (pages/index.js). The content moved here unchanged;
// "/" keeps the Organization and WebSite nodes and HandleTinyUrl (backend
// emails link to /?tinyUrlId= and /?directLoginId=), so neither is here.

const SITE = "https://www.stringssg.com"
const PAGE_URL = `${SITE}/sg`
const OG_IMAGE = "https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/female-personal-trainer-training-girl-at-gym.jpeg"

// Static - must not be new Date(), which differs between server render and
// hydration and claims the page changed on every build.
const LAST_MODIFIED = "2026-10-04T00:00:00+08:00"

const TITLE = "Home Services Singapore | Aircon Servicing & Personal Trainers"
const DESCRIPTION = "Aircon servicing from $16/unit and certified personal trainers from $84/session in Singapore. Plus handyman and part-time helper. ACRA-registered since 2018."

// Each Service carries the hub URL it belongs to, so the graph tells Google
// which page is canonical for that service instead of leaving it to guess.
const serviceCatalog = [
    {
        catalog: "Personal Training",
        name: "Personal Trainer Singapore",
        url: `${SITE}/sg/personal-trainer`,
        description: "In-house certified personal trainers for home, condo gym, or outdoor sessions",
    },
    {
        catalog: "Aircon Servicing",
        name: "Aircon Servicing Singapore",
        url: `${SITE}/sg/aircon-servicing-singapore`,
        description: "Vetted technicians and StringsSG service standards for servicing, chemical wash, repair and gas top-up",
    },
    {
        catalog: "Handyman Services",
        name: "Handyman Services Singapore",
        url: `${SITE}/sg/handyman-singapore-services`,
        description: "Background-checked professionals for plumbing, electrical, carpentry and home repairs",
    },
    {
        catalog: "Part-Time Helper",
        name: "Part-Time Helper Singapore",
        url: `${SITE}/sg/part-time-cleaner-singapore`,
        description: "Verified helpers for cleaning, ironing and household chores",
    },
]

const sgHomeSchema = {
    "@context": "https://schema.org",
    "@graph": [
        {
            // The single LocalBusiness entity for the whole site. Hub pages
            // reference this @id as their Service provider rather than
            // declaring their own business with its own rating. The @id kept
            // its original "/#localbusiness" form when the node moved here
            // from "/" - it is an identifier, and dozens of pages reference it.
            "@type": "LocalBusiness",
            "@id": `${SITE}/#localbusiness`,
            "name": legalName,
            "image": OG_IMAGE,
            "url": PAGE_URL,
            "priceRange": "$$",
            "address": {
                "@type": "PostalAddress",
                "streetAddress": branchAddress.streetAddress,
                "addressLocality": branchAddress.addressLocality,
                "postalCode": branchAddress.postalCode,
                "addressCountry": branchAddress.addressCountry,
            },
            "geo": {
                "@type": "GeoCoordinates",
                "latitude": String(branchGeo.latitude),
                "longitude": String(branchGeo.longitude),
            },
            "openingHoursSpecification": {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
                "opens": "08:00",
                "closes": "22:00",
            },
            "telephone": "+65-6680-3926",
            "parentOrganization": { "@id": `${SITE}/#organization` },
            "hasOfferCatalog": {
                "@type": "OfferCatalog",
                "name": "Professional Home Services",
                "itemListElement": serviceCatalog.map(service => ({
                    "@type": "OfferCatalog",
                    "name": service.catalog,
                    "itemListElement": [
                        {
                            "@type": "Offer",
                            "url": service.url,
                            "itemOffered": {
                                "@type": "Service",
                                "name": service.name,
                                "url": service.url,
                                "description": service.description,
                                "areaServed": { "@type": "Country", "name": "Singapore" },
                                "provider": { "@id": `${SITE}/#organization` },
                            },
                        },
                    ],
                })),
            },
        },
        {
            "@type": "WebPage",
            "@id": `${PAGE_URL}/#webpage`,
            "url": PAGE_URL,
            "name": TITLE,
            "description": "Aircon servicing from $16/unit and certified personal trainers from $84/session in Singapore, plus handyman and part-time helper services.",
            "isPartOf": { "@id": `${SITE}/#website` },
            "about": { "@id": `${SITE}/#localbusiness` },
            "inLanguage": "en-SG",
            "datePublished": "2018-01-01T00:00:00+08:00",
            "dateModified": LAST_MODIFIED,
            "breadcrumb": { "@id": `${PAGE_URL}/#breadcrumblist` },
        },
        breadcrumbJsonLd(buildBreadcrumb('/sg'), PAGE_URL),
    ],
}

export default function SingaporeHome() {

    useEffect(() => {
        const currentLocation = globalThis.location.href;
        if (currentLocation.includes("#")){
            let locationId = currentLocation.substring(currentLocation.indexOf("#") + 1);
            let ele = document.getElementById(locationId);
            if(ele){
                setTimeout(() => {
                    ele.scrollIntoView({ behavior: "smooth", block:"start"});
                }, 500)
            }
        }
    }, [])

  return (
    <>
      <Head>
        <title>{TITLE}</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content={DESCRIPTION}/>
        <meta name="author" content={legalName}/>
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta httpEquiv="content-language" content="en-sg" />

        {/* Geo Tags - Paya Lebar branch, matching the LocalBusiness address below */}
        <meta name="geo.region" content="SG" />
        <meta name="geo.placename" content="Singapore" />
        <meta name="geo.position" content={`${branchGeo.latitude};${branchGeo.longitude}`} />
        <meta name="ICBM" content={`${branchGeo.latitude}, ${branchGeo.longitude}`} />

        {/* Open Graph */}
        <meta property="og:locale" content="en_SG" />
        <meta property="og:site_name" content="StringsSG" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={TITLE}/>
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:secure_url" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="StringsSG Professional Home Services in Singapore" />

        {/* Business Contact */}
        <meta property="business:contact_data:street_address" content="60 Paya Lebar Road #06-06 Paya Lebar Square" />
        <meta property="business:contact_data:locality" content="Singapore" />
        <meta property="business:contact_data:postal_code" content={branchAddress.postalCode} />
        <meta property="business:contact_data:country_name" content="Singapore" />
        <meta property="business:contact_data:phone_number" content="+65 6680 3926" />
        <meta property="business:contact_data:website" content={PAGE_URL} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@stringssg"/>
        <meta name="twitter:creator" content="@stringssg"/>
        <meta name="twitter:url" content={PAGE_URL}/>
        <meta name="twitter:title" content={TITLE}/>
        <meta name="twitter:description" content={DESCRIPTION}/>
        <meta name="twitter:image" content={OG_IMAGE} />
        <meta name="twitter:image:alt" content="StringsSG Professional Home Services in Singapore" />

        <link rel="icon" href="/favicon.ico" />

        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(sgHomeSchema) }}
        />

        <link rel="canonical" href={PAGE_URL} />
        {COUNTRY_HOME_ALTERNATES.map((alt) => (
          <link key={alt.hreflang} rel="alternate" hrefLang={alt.hreflang} href={alt.path === '/' ? `${SITE}/` : `${SITE}${alt.path}`} />
        ))}
      </Head>

      <ModernHomePage />

      {/* Google Analytics */}
      <Script
        id="google-tagmanager-new"
        src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
        strategy="lazyOnload"
        onLoad={() => {
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-QD1HGGHDYJ');
        }}
      />
    </>
  )
}
